import React from 'react'

import { type MapContent, type MapCountry, type MapLabels } from '../../types'
import { getIndicativeCaseCount } from './mapPresentation'
import styles from './styles.module.scss'

type CountryDetailsProps = {
  country: MapCountry
  allCasesLabel: MapContent['allCasesLabel']
  allCasesUrl: MapContent['allCasesUrl']
  onClose: () => void
  labels: Required<Pick<MapLabels, 'countrySubtitle' | 'office' | 'caseCount' | 'recentCases'>>
}

const CountryDetails = ({
  country,
  allCasesLabel,
  allCasesUrl,
  onClose,
  labels,
}: CountryDetailsProps) => {
  const headingId = `country-details-${country.id}`

  return (
    <aside
      className={styles.countryDetails}
      data-testid="country-details"
      data-country-id={country.id}
      aria-labelledby={headingId}
      aria-live="polite"
    >
      <button
        type="button"
        className={styles.closeDetails}
        onClick={onClose}
        aria-label="Close country information"
      >
        ×
      </button>

      <div className={styles.countryDetailsTitle}>
        <img
          className={styles.countryFlag}
          src={country.flagImage}
          alt={`${country.name} flag`}
          width={64}
          height={42}
        />
        <div>
          <h3 id={headingId}>{country.name}</h3>
          <p>{labels.countrySubtitle}</p>
        </div>
      </div>

      {country.office ? (
        <div className={styles.cardSection}>
          <p className={styles.cardLabel}>{labels.office}</p>
          <address>{country.office.displayAddress || country.office.address}</address>
        </div>
      ) : null}

      <div className={styles.cardSection}>
        <p className={styles.cardLabel}>{labels.caseCount}</p>
        <p className={styles.caseCount}>{getIndicativeCaseCount(country)}</p>
      </div>

      {country.cases.length ? (
        <div className={styles.cardSection}>
          <p className={styles.cardLabel}>{labels.recentCases}</p>
          <ul className={styles.caseList}>
            {country.cases.slice(0, 3).map((mapCase) => (
              <li key={mapCase.title}>
                {mapCase.url ? <a href={mapCase.url}>{mapCase.title}</a> : mapCase.title}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {country.cases.length ? (
        <a className={styles.allCasesLink} href={allCasesUrl}>
          {allCasesLabel} <span aria-hidden>→</span>
        </a>
      ) : null}
    </aside>
  )
}

export default CountryDetails
